// 1. React
import React, { useCallback, useEffect, useState } from "react";

// 2. Third-party
import { Modal, Select, Button, Spin, Empty, Alert, Tag, Space, message, Form } from "antd";
import { Link } from "react-router-dom";
import moment from "moment";

// 3. Components
import SchemaFormRenderer from "@iso/components/SchemaFormRenderer";
import VisitorDetailsFields from "./VisitorDetailsFields";

// 4. Services
import { visitorInductionService } from "@iso/services";
import { visitorInfoService } from "@iso/services/visitorInfo.service";

// 5. Constants
import { getFormPurposeLabel } from "@iso/constants/formPurposes";
import { VISITOR_INFO_TYPES } from "@iso/constants/visitor";

const { Option, OptGroup } = Select;

// Where the modal is in the flow. One value rather than a pile of booleans, so
// it can never show the picker and the form at the same time.
const STEP = {
  PICK: "pick",
  // A hard-coded Visitor Detail screen between picking the induction and
  // running it, mirroring the check-in flow's `checkin_info` step: confirm and
  // top up who this person is (emergency contact especially) before asking them
  // to sign anything. These answers go to visitor_info, NOT to the submission.
  DETAILS: "details",
  RUN: "run",
};

/**
 * Run an induction against a visitor.
 *
 * Step 1 picks which induction to run — the property's forms whose purpose is
 * an induction and which have questions to answer. Step 2 renders that form so
 * staff can complete it with the visitor present, or sends the visitor a link
 * to do it themselves.
 *
 * Nothing here marks a visitor inducted on its own, and submitting is not the
 * end of it either: the record reaches `pending` on submission and only becomes
 * Completed once someone verifies the answers (VisitorViewSummary).
 */
/**
 * `visitor` and `worker` are alternatives, not both: the Visitors page runs an
 * induction against a VISIT, the contractor Personnel register runs one against
 * a WORKER who may never have checked in. Everything after the start call is
 * identical, so the two share this modal.
 */
const RunInductionModal = ({ visible, visitor, worker, propertyId, onClose, onCompleted }) => {
  const [step, setStep] = useState(STEP.PICK);
  const [forms, setForms] = useState([]);
  // The property's Areas, for any "Property Area / Location" field in whichever
  // induction is run. Arrives with the picker, so running one costs no extra
  // request.
  const [areas, setAreas] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(undefined);
  const [induction, setInduction] = useState(null);
  const [runningForm, setRunningForm] = useState(null);
  const [loadingForms, setLoadingForms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detailsForm] = Form.useForm();
  const detailsType = Form.useWatch("visitor_type", detailsForm) || VISITOR_INFO_TYPES.VISITOR;

  const isWorker = !!worker;
  const visitorName =
    (worker && worker.full_name) || (visitor && visitor.name) || "this visitor";

  // The only call that differs between the two subjects.
  const startCall = (formId) =>
    isWorker
      ? visitorInductionService.startForWorker(worker.id, formId)
      : visitorInductionService.start(visitor.id, formId);

  const loadForms = useCallback(async () => {
    if (!propertyId) return;
    setLoadingForms(true);
    try {
      const res = await visitorInductionService.getAvailableForms(propertyId);
      setForms((res && res.success && res.data && res.data.forms) || []);
      setAreas((res && res.success && res.data && res.data.areas) || []);
    } catch (e) {
      console.error("Failed to load induction forms:", e);
      setForms([]);
      setAreas([]);
    } finally {
      setLoadingForms(false);
    }
  }, [propertyId]);

  // Reset to a clean picker every time the modal is opened, so a previous
  // visitor's induction can never be left on screen for the next one.
  useEffect(() => {
    if (!visible) return;
    setStep(STEP.PICK);
    setSelectedFormId(undefined);
    setInduction(null);
    setRunningForm(null);
    loadForms();
    // Seed the details step from the visitor we already hold, so staff confirm
    // and top up rather than retype.
    detailsForm.setFieldsValue({
      visitor_type: (visitor && visitor.visitor_type) || VISITOR_INFO_TYPES.VISITOR,
      name: visitor && visitor.name,
      email: visitor && visitor.email,
      phone: visitor && visitor.phone,
      business_name: visitor && visitor.business_name,
      birthday: visitor && visitor.birthday ? moment(visitor.birthday) : undefined,
      emergency_name: visitor && visitor.emergency_name,
      emergency_contact: visitor && visitor.emergency_contact,
      emergency_mobile_number: visitor && visitor.emergency_mobile_number,
      // Read-only here; shown for context only.
      scheduled_arrival:
        visitor && (visitor.scheduled_arrival || visitor.checkin_time)
          ? moment(visitor.scheduled_arrival || visitor.checkin_time)
          : moment(),
    });
  }, [visible, loadForms, visitor, detailsForm]);

  // Picking an induction moves to the details step. No record is created yet —
  // the visitor's details are confirmed first, so an abandoned details screen
  // does not leave a Pending induction behind.
  const goToDetails = () => {
    if (!selectedFormId) {
      message.warning("Choose an induction to run");
      return;
    }
    // A worker-owned induction has no visitor_info row behind it, so the
    // details step has nothing to confirm or save to. startInduction() moves to
    // the runner itself once the form has loaded — setting the step here too
    // would render the runner before there is a form to draw.
    if (isWorker) {
      startInduction();
      return;
    }
    setStep(STEP.DETAILS);
  };

  /**
   * Persist the details step to visitor_info. Returns false when validation
   * fails or the save is rejected, so the caller stays on the step.
   *
   * visitor_type and scheduled_arrival are deliberately NOT sent: both are
   * read-only here. Type decides which inductions the property requires, and
   * arrival belongs to the visit, not to this induction.
   */
  const saveDetails = async () => {
    let values;
    try {
      values = await detailsForm.validateFields();
    } catch (e) {
      return false;
    }

    const payload = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      business_name: values.business_name,
      birthday: values.birthday ? values.birthday.format("YYYY-MM-DD") : null,
      emergency_name: values.emergency_name,
      emergency_contact: values.emergency_contact,
      emergency_mobile_number: values.emergency_mobile_number,
    };

    try {
      const res = await visitorInfoService.update({ visitor_info: payload }, visitor.id);
      if (res && res.code === "0000") {
        return true;
      }
      message.error((res && res.message) || "Could not save the visitor's details");
      return false;
    } catch (e) {
      message.error("Could not save the visitor's details");
      return false;
    }
  };

  // Save the confirmed details to visitor_info, then create the induction
  // record and render its form. The record exists from this point on as
  // Pending — starting an induction and abandoning it is itself worth seeing on
  // the visitor.
  const startInduction = async () => {
    if (!selectedFormId) {
      message.warning("Choose an induction to run");
      return;
    }
    setBusy(true);
    try {
      // Only a visit has details to confirm.
      if (!isWorker) {
        const saved = await saveDetails();
        if (!saved) {
          setBusy(false);
          return;
        }
      }
      const res = await startCall(selectedFormId);
      if (res && res.success) {
        setInduction(res.data.induction);
        setRunningForm(res.data.form);
        setStep(STEP.RUN);
      } else {
        message.error((res && res.message) || "Could not start the induction");
      }
    } catch (e) {
      console.error("Failed to start induction:", e);
      message.error("Could not start the induction");
    } finally {
      setBusy(false);
    }
  };

  const submitInduction = async (answers) => {
    setBusy(true);
    try {
      const res = await visitorInductionService.submit(induction.id, answers);
      if (res && res.success) {
        // NOT "completed". Submitting is the visitor's half; an owner/admin
        // (or whoever ran it) still has to accept the answers before this
        // person counts as inducted. Saying "completed" here is what made
        // staff think the job was done and never open the record again.
        message.success(
          `Induction submitted for ${visitorName} — it now needs verifying`
        );
        onCompleted && onCompleted();
        onClose && onClose();
      } else {
        message.error((res && res.message) || "Could not record the induction");
      }
    } catch (e) {
      console.error("Failed to submit induction:", e);
      message.error("Could not record the induction");
    } finally {
      setBusy(false);
    }
  };

  // Send the visitor the link instead of completing it here. Needs a record, so
  // it starts one first when the user is still on the picker.
  const sendLink = async (channel) => {
    setBusy(true);
    try {
      let target = induction;
      if (!target) {
        if (!selectedFormId) {
          message.warning("Choose an induction to send");
          return;
        }
        const started = await startCall(selectedFormId);
        if (!started || !started.success) {
          message.error((started && started.message) || "Could not start the induction");
          return;
        }
        target = started.data.induction;
      }
      const res = await visitorInductionService.invite(target.id, channel);
      if (res && res.success) {
        message.success(`Induction link sent to ${visitorName}`);
        onCompleted && onCompleted();
        onClose && onClose();
      } else {
        message.error((res && res.message) || "Could not send the induction link");
      }
    } catch (e) {
      console.error("Failed to send induction link:", e);
      message.error("Could not send the induction link");
    } finally {
      setBusy(false);
    }
  };

  const renderPicker = () => {
    if (loadingForms) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
          <Spin />
        </div>
      );
    }

    if (!forms.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              This property has no induction forms yet.
              <br />
              Build one under <Link to="/manage-forms">Forms</Link> and give it an
              induction purpose.
            </span>
          }
        />
      );
    }

    // What this property requires of THIS visitor, and which of those they
    // already hold — both come from the row's induction_summary, so the picker
    // agrees with the badge without a second request.
    const items = (visitor && visitor.induction_summary && visitor.induction_summary.items) || [];
    const requiredFormIds = new Set(items.map((i) => i.form_id));
    // `pending` counts as held: the visitor has done it and is waiting on a
    // verifier (VER-D6). Leaving it out would invite staff to run the same
    // induction a second time purely because nobody has accepted the first —
    // and a second run does not make the first one verified.
    const heldFormIds = new Set(
      items
        .filter((i) => i.state === "completed" || i.state === "expiring")
        .map((i) => i.form_id)
    );
    const awaitingFormIds = new Set(
      items.filter((i) => i.state === "pending").map((i) => i.form_id)
    );
    const required = forms.filter((f) => requiredFormIds.has(f.id));
    const optional = forms.filter((f) => !requiredFormIds.has(f.id));

    return (
      <>
        <p style={{ marginBottom: 8 }}>
          Which induction should {visitorName} complete?
        </p>
        <Select
          showSearch
          optionFilterProp="children"
          style={{ width: "100%" }}
          placeholder="Select an induction"
          value={selectedFormId}
          onChange={setSelectedFormId}
        >
          {required.length > 0 && (
            <OptGroup label="Required for this visitor">
              {required.map((f) => (
                <Option key={f.id} value={f.id}>
                  {heldFormIds.has(f.id) ? "✓ " : awaitingFormIds.has(f.id) ? "◑ " : ""}
                  {f.name}
                  <Tag style={{ marginLeft: 8 }}>{getFormPurposeLabel(f.purpose)}</Tag>
                  {awaitingFormIds.has(f.id) && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>
                      Awaiting verification
                    </Tag>
                  )}
                </Option>
              ))}
            </OptGroup>
          )}
          {required.length > 0 ? (
            optional.length > 0 && (
              <OptGroup label="Other inductions">
                {optional.map((f) => (
                  <Option key={f.id} value={f.id}>
                    {f.name}
                    <Tag style={{ marginLeft: 8 }}>{getFormPurposeLabel(f.purpose)}</Tag>
                  </Option>
                ))}
              </OptGroup>
            )
          ) : (
            // No requirement set on this property — one flat list, as before.
            optional.map((f) => (
              <Option key={f.id} value={f.id}>
                {f.name}
                <Tag style={{ marginLeft: 8 }}>{getFormPurposeLabel(f.purpose)}</Tag>
              </Option>
            ))
          )}
        </Select>
      </>
    );
  };

  const renderDetails = () => (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Confirm this visitor's details before the induction"
        description="Anything you add here is saved to the visitor record, not to the induction answers."
      />
      <Form form={detailsForm} layout="vertical" name="inductionVisitorDetails">
        <VisitorDetailsFields
          form={detailsForm}
          visitorType={detailsType}
          typeReadOnly
          arrivalReadOnly
        />
      </Form>
    </>
  );

  const renderRunner = () => (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`${runningForm && runningForm.name} — ${visitorName}`}
        description="Submitting records the answers. Someone then has to verify them — from View Summary — before this counts as inducted."
      />
      <SchemaFormRenderer
        form={runningForm}
        areas={areas}
        loading={busy}
        onSubmit={submitInduction}
      />
    </>
  );

  const pickerFooter = [
    <Button key="cancel" onClick={onClose} disabled={busy}>
      Cancel
    </Button>,
    <Button
      key="link"
      onClick={() => sendLink("email")}
      disabled={busy || !selectedFormId}
    >
      Send link instead
    </Button>,
    <Button
      key="start"
      type="primary"
      loading={busy}
      disabled={!selectedFormId}
      onClick={goToDetails}
    >
      Next
    </Button>,
  ];

  const detailsFooter = [
    <Button key="back" onClick={() => setStep(STEP.PICK)} disabled={busy}>
      Back
    </Button>,
    <Button key="start" type="primary" loading={busy} onClick={startInduction}>
      Save &amp; start induction
    </Button>,
  ];

  return (
    <Modal
      title="Run induction"
      open={visible}
      onCancel={onClose}
      width={step === STEP.PICK ? 520 : 760}
      destroyOnClose
      // The renderer draws its own Next/Submit controls, so the modal footer
      // would be a second, conflicting set of buttons on that step.
      footer={
        step === STEP.PICK ? (
          pickerFooter
        ) : step === STEP.DETAILS ? (
          detailsFooter
        ) : (
          <Space>
            <Button onClick={onClose} disabled={busy}>
              Close
            </Button>
            <Button onClick={() => sendLink("email")} disabled={busy}>
              Send link instead
            </Button>
          </Space>
        )
      }
    >
      {step === STEP.PICK
        ? renderPicker()
        : step === STEP.DETAILS
        ? renderDetails()
        : renderRunner()}
    </Modal>
  );
};

export default RunInductionModal;
